import { Package, Activity, CheckCircle, DollarSign, Users, Clock } from 'lucide-react';
import { KpiCard } from './KpiCard';
import { ChartCard } from './ChartCard';
import { ShipmentTrendChart } from '../charts/ShipmentTrendChart';
import { StatusDonutChart } from '../charts/StatusDonutChart';
import { RevenueTrendChart } from '../charts/RevenueTrendChart';
import { TopCitiesChart } from '../charts/TopCitiesChart';
import { CarrierPerformanceChart } from '../charts/CarrierPerformanceChart';
import {
  useDashboardKpis,
  useShipmentTrend,
  useRevenueTrend,
  useStatusDistribution,
  useTopCities,
  useCarrierPerformance,
  useRecentActivity,
} from '@/hooks/useDashboardAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/formatters';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_LABELS: Record<string, string> = {
  delivered: 'تم التوصيل', in_transit: 'قيد النقل', assigned: 'مُعيَّن',
  picked_up: 'تم الاستلام', pending: 'قيد الانتظار', failed: 'فشل', cancelled: 'ملغى',
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  delivered: 'default', failed: 'destructive', cancelled: 'outline', pending: 'secondary',
};

export function AdminDashboard() {
  const { data: kpis, loading: kpiLoading } = useDashboardKpis('today');
  const { data: trend, loading: trendLoading } = useShipmentTrend(30);
  const { data: revenue, loading: revenueLoading } = useRevenueTrend(30);
  const { data: statusDist, loading: statusLoading } = useStatusDistribution();
  const { data: cities, loading: citiesLoading } = useTopCities(10);
  const { data: carriers, loading: carriersLoading } = useCarrierPerformance();
  const { data: activity, loading: activityLoading } = useRecentActivity(10);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {kpiLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))
        ) : (
          <>
            <KpiCard
              title="شحنات اليوم"
              value={kpis?.today_shipments?.value ?? 0}
              trend={kpis?.today_shipments?.trend}
              icon={Package}
              gradient="from-blue-500 to-blue-600"
            />
            <KpiCard
              title="تسليمات نشطة"
              value={kpis?.active_deliveries?.value ?? 0}
              icon={Activity}
              gradient="from-purple-500 to-purple-600"
            />
            <KpiCard
              title="نسبة التوصيل"
              value={kpis?.success_rate?.value ?? 0}
              format="percent"
              icon={CheckCircle}
              gradient="from-green-500 to-green-600"
              label={kpis?.success_rate?.label}
            />
            <KpiCard
              title="رسوم المنصة اليوم"
              value={kpis?.revenue_today?.value ?? 0}
              trend={kpis?.revenue_today?.trend}
              format="currency"
              icon={DollarSign}
              gradient="from-emerald-500 to-emerald-600"
              label={kpis?.revenue_today?.label}
            />
            <KpiCard
              title="السائقون النشطون"
              value={kpis?.active_drivers?.value ?? 0}
              icon={Users}
              gradient="from-cyan-500 to-cyan-600"
            />
            <KpiCard
              title="تسويات بانتظار المراجعة"
              value={kpis?.pending_settlement_amount?.count ?? 0}
              format="number"
              icon={Clock}
              gradient="from-orange-500 to-orange-600"
              label={kpis?.pending_settlement_amount?.amount > 0 ? `${Number(kpis?.pending_settlement_amount?.amount).toLocaleString()} إجمالي` : undefined}
            />
          </>
        )}
      </div>

      {/* Row 1: Trend + Donut */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <ChartCard title="حجم الشحنات — آخر 30 يوم" loading={trendLoading} className="lg:col-span-2">
          <ShipmentTrendChart data={trend} days={30} />
        </ChartCard>
        <ChartCard title="توزيع الحالات" loading={statusLoading}>
          <StatusDonutChart data={statusDist} />
        </ChartCard>
      </div>

      {/* Row 2: Revenue + Cities */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <ChartCard title="الإيرادات — آخر 30 يوم" loading={revenueLoading}>
          <RevenueTrendChart data={revenue} showPlatformFees />
        </ChartCard>
        <ChartCard title="أكثر مدن التوصيل" loading={citiesLoading}>
          <TopCitiesChart data={cities} />
        </ChartCard>
      </div>

      {/* Row 3: Carrier Performance + Recent Activity */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <ChartCard title="أداء الناقلين — آخر 30 يوم" loading={carriersLoading}>
          <CarrierPerformanceChart data={carriers} />
        </ChartCard>

        {/* Recent Activity Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground/80">آخر التحديثات</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {activityLoading ? (
              <Skeleton className="h-[220px] w-full rounded-lg" />
            ) : activity.length === 0 ? (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">لا توجد تحديثات</div>
            ) : (
              <div className="space-y-2 max-h-[220px] overflow-y-auto pe-1">
                {activity.map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant={STATUS_VARIANT[item.status_code] ?? 'secondary'} className="text-[10px] shrink-0">
                        {STATUS_LABELS[item.status_code] ?? item.status_code}
                      </Badge>
                      <span className="text-xs text-muted-foreground truncate">{item.tracking_number}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {formatDate(new Date(item.created_at), 'HH:mm')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
